import {
  type Campo,
  conCalculados,
  type Formulario,
  pasosDe,
  seEnseña,
  validarEnvio,
} from '@mc/formularios'

/**
 * La vista previa de un formulario **con las reglas de verdad**: los pasos, la lógica
 * condicional, los cálculos y la validación son los de `@mc/formularios`, el mismo código
 * que valida el núcleo y que pinta la web (§24). Aquí solo se pasa el documento tal como
 * lo edita el panel (con `blockType`) a la forma que entiende ese paquete, y se reparte lo
 * que devuelve por pasos.
 *
 * Funciones puras, con su prueba (`previa.test.ts`).
 */
type Fila = Record<string, unknown>

const lista = (v: unknown): Fila[] =>
  Array.isArray(v) ? (v as Fila[]).filter((x) => x && typeof x === 'object') : []

/**
 * Los campos del documento como los entiende `@mc/formularios`: el `blockType` de Payload es
 * el `tipo`, y sin los `id` de las filas. Es lo mismo que hace el núcleo al leer un
 * formulario guardado (`camposDelFormulario`), que no se puede importar desde aquí.
 */
export const camposDe = (guardados: unknown): Campo[] =>
  lista(guardados).map((fila) => {
    const { blockType, id: _id, _clave: _c, ...resto } = fila
    const campo = { ...resto, tipo: blockType } as unknown as Campo
    if (Array.isArray(resto.campos))
      campo.campos = lista(resto.campos).map(
        ({ id: _i, _clave: _k, ...hijo }) => hijo as unknown as Campo,
      )
    if (typeof resto.extensiones === 'string') campo.extensiones = [resto.extensiones]
    if (Array.isArray(resto.opciones))
      campo.opciones = lista(resto.opciones).map((o) => ({
        etiqueta: String(o.etiqueta ?? ''),
        valor: String(o.valor ?? ''),
      }))
    const condicion = resto.condicion as null | undefined | { modo?: unknown; reglas?: unknown }
    if (condicion && typeof condicion === 'object') {
      campo.condicion = {
        modo: condicion.modo === 'alguna' ? 'alguna' : 'todas',
        reglas: lista(condicion.reglas).map((r) => ({
          campo: String(r.campo ?? ''),
          operador: (r.operador ?? 'igual') as never,
          valor: r.valor === undefined || r.valor === null ? null : String(r.valor),
        })),
      }
    }
    return campo
  })

/** El formulario entero (lo que hace falta para validar un envío), desde el documento del editor. */
export const formularioDe = (documento: Fila): Formulario => ({
  id: typeof documento.id === 'number' || typeof documento.id === 'string' ? documento.id : 'nuevo',
  nombre: String(documento.nombre ?? ''),
  slug: String(documento.slug ?? ''),
  campos: camposDe(documento.campos),
  confirmacion: (documento.confirmacion as Formulario['confirmacion']) ?? null,
  estado: typeof documento.estado === 'string' ? documento.estado : null,
})

export type Paso = { titulo: null | string; campos: Campo[] }

/**
 * Los pasos con su título: el de cada «salto de paso» es el del paso que empieza ahí; el
 * primero no tiene. Los campos de cada paso son exactamente los de `pasosDe`.
 */
export const pasosConTitulo = (campos: Campo[]): Paso[] => {
  const titulos: Array<null | string> = [null]
  const crudos: Campo[][] = [[]]
  for (const campo of campos) {
    if (campo.tipo === 'paso') {
      titulos.push(campo.etiqueta || null)
      crudos.push([])
    } else crudos[crudos.length - 1]!.push(campo)
  }
  const deVerdad = pasosDe(campos)
  const titulosQueQuedan = titulos.filter((_, i) => crudos[i]!.length > 0 || i === 0)
  return deVerdad.map((campos, i) => ({ titulo: titulosQueQuedan[i] ?? null, campos }))
}

/** Los valores con los campos calculados ya puestos: lo que se enseña mientras se rellena. */
export const conTotales = (campos: Campo[], valores: Fila): Fila => conCalculados(campos, valores)

/**
 * Tipos que la vista previa no manda al validador del paquete: su respuesta tiene una forma
 * que todavía se está decidiendo (un lugar: país y niveles), y un error de forma diría «está
 * mal» a algo que está bien. De esos, aquí solo se mira si falta lo obligatorio.
 */
const COMPROBACION_PROPIA = new Set(['lugar'])

const vacio = (v: unknown) =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)

/**
 * Los errores de un paso: se valida el formulario entero con `validarEnvio` (las condiciones
 * pueden mirar campos de otros pasos) y se quedan los de los campos de este paso. Un error
 * de un repetidor (`personas.0.nombre`) es de su campo (`personas`).
 */
export const erroresDelPaso = (
  formulario: Formulario,
  valores: Fila,
  paso: Campo[],
): Record<string, string> => {
  const claves = new Set(paso.map((c) => c.clave))
  const propios = formulario.campos.filter((c) => COMPROBACION_PROPIA.has(c.tipo))
  const errores: Record<string, string> = {}
  const resultado = validarEnvio(formulario, valores)
  if (!resultado.ok) {
    for (const e of resultado.errores) {
      const raiz = e.clave.split('.')[0] ?? e.clave
      if (!claves.has(raiz) || propios.some((c) => c.clave === raiz)) continue
      errores[e.clave] ??= e.mensaje
    }
  }
  for (const c of propios) {
    if (claves.has(c.clave) && c.obligatorio && vacio(valores[c.clave]) && seEnseña(c, valores))
      errores[c.clave] = 'Elige una opción.'
  }
  return errores
}

/**
 * Si el texto de un botón del color de marca va en claro o en oscuro: el que se lea mejor
 * sobre ese color (luminancia relativa de WCAG). Un color que no se entiende, en claro.
 */
export const textoSobre = (hex: string): 'claro' | 'oscuro' => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return 'claro'
  const h = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join('') : m[1]!
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
  const luz = 0.2126 * r + 0.7152 * g + 0.0722 * b
  // Contraste con blanco frente a contraste con negro.
  return 1.05 / (luz + 0.05) >= (luz + 0.05) / 0.05 ? 'claro' : 'oscuro'
}

/** El color de marca de Ajustes, solo si es un color escrito como tal: va a una variable de CSS. */
export const colorDeMarca = (valor: unknown): null | string =>
  typeof valor === 'string' && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(valor.trim())
    ? valor.trim()
    : null
